import React, { useState, useRef, useEffect } from 'react';
import './InPlaceNumberEditor.css';

/**
 * InPlaceNumberEditor provides a simple interface for editing numbers in-place
 * Click to edit, Enter/blur to save, Esc to cancel
 * 
 * @param {Object} props - Component props
 * @param {number} props.value - The current number value
 * @param {Function} props.onSave - Callback when value is saved (receives new value)
 * @param {string} props.className - Optional additional CSS class
 * @returns {JSX.Element} InPlaceNumberEditor component
 */
const InPlaceNumberEditor = ({ value, onSave, className = '' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef(null);
  
  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  // Start editing
  const handleStartEdit = () => {
    setInputValue(value);
    setIsEditing(true);
  };
  
  // Save changes
  const handleSave = () => {
    const newValue = parseInt(inputValue, 10);
    if (!isNaN(newValue) && newValue > 0) {
      onSave(newValue);
    } else {
      // Revert to original value if invalid
      setInputValue(value);
    }
    setIsEditing(false);
  };
  
  // Cancel editing
  const handleCancel = () => {
    setInputValue(value);
    setIsEditing(false);
  };
  
  // Handle input changes
  const handleChange = (e) => {
    setInputValue(e.target.value);
  };
  
  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  
  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="1"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`in-place-editor-input ${className}`}
      />
    );
  }
  
  return (
    <span 
      className={`in-place-editor-display ${className}`} 
      onClick={handleStartEdit}
    >
      {value}
    </span>
  );
};

export default InPlaceNumberEditor; 